// LxDialog — Lamtix-token-wrapped Dialog primitive
//
// RTL audit:
//   - DialogHeader: text-start (logical, RTL-safe)
//   - Close button: end-4 top-4 (logical positioning — flips in RTL)
//   - Content centered via start-[50%] (logical) + translate — correct in RTL
//   - DialogFooter: sm:justify-end (logical) — items flow from inline-end
//   - No raw Tailwind colors — semantic only
//   - confirm/discard action slots (LxConfirmDialog preset)
//
// D-SHADCN-LAMTIX-WRAPPED-01: sole import path is @lamtix/ui
import * as React from 'react';
import { cn } from '../utils';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from '../shadcn/dialog';

// ── Re-export sub-parts with Lx prefix ───────────────────────────────────────

const LxDialogTrigger = DialogTrigger;
const LxDialogClose = DialogClose;
const LxDialogPortal = DialogPortal;
const LxDialogOverlay = DialogOverlay;

// ── LxDialogContent — with Lamtix max-width + text-start header ──────────────

const LxDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogContent>,
  React.ComponentPropsWithoutRef<typeof DialogContent>
>(({ className, ...props }, ref) => (
  <DialogContent
    ref={ref}
    className={cn(
      'sm:max-w-md border-border bg-background text-foreground',
      // Override default shadcn max-w-lg for a tighter modal
      className,
    )}
    {...props}
  />
));
LxDialogContent.displayName = 'LxDialogContent';

const LxDialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <DialogHeader
    className={cn('text-start gap-2', className)}
    {...props}
  />
);
LxDialogHeader.displayName = 'LxDialogHeader';

const LxDialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <DialogFooter
    className={cn('gap-2 sm:gap-0 sm:space-x-0 sm:justify-end', className)}
    {...props}
  />
);
LxDialogFooter.displayName = 'LxDialogFooter';

const LxDialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogTitle>,
  React.ComponentPropsWithoutRef<typeof DialogTitle>
>(({ className, ...props }, ref) => (
  <DialogTitle
    ref={ref}
    className={cn('text-start text-lg font-semibold text-foreground', className)}
    {...props}
  />
));
LxDialogTitle.displayName = 'LxDialogTitle';

const LxDialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogDescription>,
  React.ComponentPropsWithoutRef<typeof DialogDescription>
>(({ className, ...props }, ref) => (
  <DialogDescription
    ref={ref}
    className={cn('text-start text-sm text-muted-foreground', className)}
    {...props}
  />
));
LxDialogDescription.displayName = 'LxDialogDescription';

// ── LxConfirmDialog — confirm/discard preset (used for discard changes, etc.) ─

export interface LxConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel?: () => void;
  variant?: 'default' | 'destructive';
  children?: React.ReactNode;
}

function LxConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'تأكيد',
  cancelLabel = 'إلغاء',
  onConfirm,
  onCancel,
  variant = 'default',
  children,
}: LxConfirmDialogProps) {
  function handleConfirm() {
    onConfirm();
    onOpenChange(false);
  }
  function handleCancel() {
    onCancel?.();
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <LxDialogContent>
        <LxDialogHeader>
          <LxDialogTitle>{title}</LxDialogTitle>
          {description && <LxDialogDescription>{description}</LxDialogDescription>}
        </LxDialogHeader>
        <LxDialogFooter>
          <button
            type="button"
            onClick={handleCancel}
            className="inline-flex min-h-[44px] items-center justify-center rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-muted transition-colors"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className={cn(
              'inline-flex min-h-[44px] items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors',
              variant === 'destructive'
                ? 'bg-destructive text-destructive-foreground hover:bg-lx-danger-600'
                : 'bg-primary text-primary-foreground hover:bg-lx-primary-600',
            )}
          >
            {confirmLabel}
          </button>
        </LxDialogFooter>
      </LxDialogContent>
    </Dialog>
  );
}

export {
  Dialog as LxDialog,
  LxDialogTrigger,
  LxDialogClose,
  LxDialogPortal,
  LxDialogOverlay,
  LxDialogContent,
  LxDialogHeader,
  LxDialogFooter,
  LxDialogTitle,
  LxDialogDescription,
  LxConfirmDialog,
};
